import { createContext, useCallback, useContext, useMemo } from "react";

import { canUserPerformAction } from "access-roles/general-functions";
import { FAF_PROSPECT_ROLES } from "access-roles/roles";
import { useUserContext } from "api/user/UserProvider";
import { toProspectPayload } from "utils/fafHelper";
import {
  createProspectQuery,
  deleteProspectQuery,
  getProspectsByDeclarationQuery,
  updateProspectQuery,
} from "./queries";

const ProspectContext = createContext({});

export const useProspectContext = () => useContext(ProspectContext);

const isProspectChanged = (loaded, current) =>
  JSON.stringify(toProspectPayload(loaded)) !==
  JSON.stringify(toProspectPayload(current));

// Candidate prospecting (sourcing) — datatrack service. The wizard holds the
// editable state; the provider only reads and synchronizes (create/update/delete)
// with per-action role guards. Back mutations allowed on the owned draft.
export const ProspectProvider = ({ ...props }) => {
  const { userRoleList } = useUserContext();

  const getProspectsByDeclaration = useCallback(
    async (declarationId) => {
      if (
        !declarationId ||
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_PROSPECT_ROLES.ROLE_GET_PROSPECT,
        })
      )
        return [];
      try {
        return (await getProspectsByDeclarationQuery({ declarationId })) || [];
      } catch (e) {
        console.error("getProspectsByDeclaration", e);
        return [];
      }
    },
    [userRoleList]
  );

  // Synchronizes the step's prospects (create/update/delete) against the last
  // synchronized snapshot, so that going back and forth on the step does not recreate a duplicate:
  //  - prospect without id → POST (we grab the returned id and reinject it);
  //  - prospect with id    → PUT (only if it changed);
  //  - prospect present in the snapshot but removed from the list → DELETE.
  // Returns the reconciled list (ids filled in): the caller keeps it as the new snapshot.
  const syncProspects = useCallback(
    async ({ declarationId, prospects, loaded }) => {
      const can = (role) =>
        canUserPerformAction({ userRoles: userRoleList, requiredRoles: role });
      // Mutation guard fails loudly (vs a read → []): if the diff needs an operation the
      // user cannot perform, throw before touching the server so the wizard blocks the step
      // instead of silently persisting nothing (see DeclarationProvider.createDeclaration).
      const requireRole = (role) => {
        if (!can(role)) throw new Error("faf_missing_role");
      };
      const loadedList = loaded || [];
      const currentList = prospects || [];
      const keptIds = currentList.filter((p) => p.id).map((p) => p.id);
      const loadedById = new Map(loadedList.map((l) => [l.id, l]));

      const toDelete = loadedList.filter((l) => l.id && !keptIds.includes(l.id));
      const toCreate = currentList.filter((p) => !p.id);
      const toUpdate = currentList.filter(
        (p) => p.id && isProspectChanged(loadedById.get(p.id) || {}, p)
      );
      if (toDelete.length) requireRole(FAF_PROSPECT_ROLES.ROLE_DELETE_PROSPECT);
      if (toUpdate.length) requireRole(FAF_PROSPECT_ROLES.ROLE_UPDATE_PROSPECT);
      if (toCreate.length) requireRole(FAF_PROSPECT_ROLES.ROLE_CREATE_PROSPECT);

      await Promise.all(toDelete.map((l) => deleteProspectQuery({ id: l.id })));

      return Promise.all(
        currentList.map(async (p) => {
          if (p.id) {
            if (isProspectChanged(loadedById.get(p.id) || {}, p))
              await updateProspectQuery({
                id: p.id,
                payload: toProspectPayload(p),
              });
            return p;
          }
          const created = await createProspectQuery({
            payload: { ...toProspectPayload(p), declaration_id: declarationId },
          });
          return created?.id ? { ...p, id: created.id } : p;
        })
      );
    },
    [userRoleList]
  );

  const values = useMemo(
    () => ({ getProspectsByDeclaration, syncProspects }),
    [getProspectsByDeclaration, syncProspects]
  );

  return <ProspectContext.Provider value={values} {...props} />;
};
